import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import cn from 'classnames';
import { Pathname } from 'Routes';
import { saveUserOccasions } from 'Api/apiFunctions';
import LightRays from 'Components/reactbits/LightRays';
import {
  AnimatedGroup,
  AnimatedPlay,
  AnimatedAlbum,
  AnimatedGift,
  AnimatedPublic,
  AnimatedUpload,
  AnimatedShare,
  AnimatedFileText,
  AnimatedProjector,
} from 'Components/reactbits/AnimatedIcons';
import { AuthPage } from '../components/page';
import theme from '../components/auth-theme.module.css';
import styles from './index.module.css';

// Reasons a new user is joining Projector — drives the purpose-selection step.
// Keep this at 9 items so the 3x3 grid layout and fade-in timing stay intact.
// Each carries an icon from the shared animated-icon library; the labels are
// unchanged and remain the source of truth for what gets saved.
const OCCASIONS = [
  { label: 'Family Memories', Icon: AnimatedGroup },
  { label: 'Home Videos', Icon: AnimatedPlay },
  { label: 'Photos & Albums', Icon: AnimatedAlbum },
  { label: 'Special Events', Icon: AnimatedGift },
  { label: 'Travel & Trips', Icon: AnimatedPublic },
  { label: 'Digitizing Old Media', Icon: AnimatedUpload },
  { label: 'Sharing With Family', Icon: AnimatedShare },
  { label: 'Family Stories', Icon: AnimatedFileText },
  { label: 'Just Exploring', Icon: AnimatedProjector },
];

// Tick shown on a chosen card (presentational only).
const CheckIcon = ({ size = 13 }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="3.2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M20 6 9 17l-5-5" />
  </svg>
);

// Occasion-selection step for the sign-up flow. Same UI as the gift flow's
// "Is This For A Special Occasion?" step (grey buttons, blue when selected).
// On Continue we proceed to the gift-content page (the Welcome/Congrats screen
// is intentionally disconnected for now). Because this is a brand-new normal
// user — not a gift recipient — they have no uploaded content yet, so we land
// them straight on the upload grid via the `newUserUpload` route flag.
export const ChooseOccasion = () => {
  const navigate = useNavigate();
  const [selectedOccasions, setSelectedOccasions] = useState([]);
  // Reveal the Continue button only after the occasion buttons finish their
  // fade-in, mirroring the gift flow. Last button (index 8) delay 2.4s +
  // 1.6s animation; gift flow uses a 2900ms timer.
  const [gridAnimationComplete, setGridAnimationComplete] = useState(false);
  // Hovered card — drives its animated icon, as elsewhere in the app.
  const [hoveredOccasion, setHoveredOccasion] = useState(null);

  useEffect(() => {
    const timer = setTimeout(() => setGridAnimationComplete(true), 2900);
    return () => clearTimeout(timer);
  }, []);

  const toggleOccasion = (occasion) => {
    setSelectedOccasions((prev) => (prev.includes(occasion)
      ? prev.filter((occ) => occ !== occasion)
      : [...prev, occasion]));
  };

  const handleContinue = () => {
    // Keep the selection available for later steps without changing the flow.
    try {
      sessionStorage.setItem('selectedOccasions', JSON.stringify(selectedOccasions));
      // Marks the gift-content page as the new normal-user signup upload flow
      // (survives refresh, unlike location.state) so it shows the placeholder
      // folder / gift-style upload view rather than the generic progress tab.
      sessionStorage.setItem('newUserUploadFlow', 'true');
    } catch (e) {
      // sessionStorage may be unavailable (private mode) — selection is optional.
    }
    // Persist the picked occasions to the backend. Fire-and-forget: we do NOT
    // await it or block navigation on it — the endpoint may not exist yet, and
    // a failure here must never stop the user moving to the upload step.
    saveUserOccasions({ occasions: selectedOccasions })
      .then(() => console.log('[CHOOSE_OCCASION] Occasions saved:', selectedOccasions))
      .catch((err) => console.warn('[CHOOSE_OCCASION] Failed to save occasions (non-blocking):', err));
    navigate(Pathname.giftFlow.content, { state: { newUserUpload: true } });
  };

  const goBack = () => navigate(Pathname.authentication.avatar, { state: { fromVerificationCode: true } });

  return (
    <AuthPage className={theme.page} contentClassName={theme.content}>
      {/* Same backdrop as the sign-in card — light rays over the near-black
          base, identical props. */}
      <div className={styles.raysLayer} aria-hidden="true">
        <LightRays
          raysOrigin="top-center"
          raysColor="#ffffff"
          raysSpeed={0.9}
          lightSpread={0.85}
          rayLength={1.25}
          fadeDistance={1.1}
          saturation={1}
          followMouse
          mouseInfluence={0.08}
          noiseAmount={0.08}
          distortion={0.04}
        />
      </div>

      {/* Back to the avatar step. Carry fromVerificationCode so the avatar's
          Continue routes forward to this occasion page again (keeps the flow).
          Now the auth flow's glass back pill instead of grey text. */}
      <div
        role="button"
        tabIndex={0}
        aria-label="Back"
        className={cn(theme.back, styles.backLink)}
        onClick={goBack}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') goBack(); }}
      >
        BACK
      </div>

      <div className={styles.shell}>
        <header className={styles.header}>
          <h2 className={styles.stepSubHeading}>What Brings You To Projector?</h2>
          <p className={theme.subtitle}>Select one or more so we can personalize your experience</p>
        </header>

        {/* The nine reasons as wide pills sitting straight on the backdrop — the
            wrapper carries no surface of its own, so nothing frames them. */}
        <div className={styles.panel}>
          <div className={styles.occasionGrid}>
            {OCCASIONS.map(({ label, Icon }, index) => {
              const isSelected = selectedOccasions.includes(label);
              return (
                <button
                  key={label}
                  type="button"
                  aria-pressed={isSelected}
                  className={cn(styles.occasionButton, styles.buttonFadeIn, {
                    [styles.occasionButtonSelected]: isSelected,
                  })}
                  onClick={() => toggleOccasion(label)}
                  onMouseEnter={() => setHoveredOccasion(label)}
                  onMouseLeave={() => setHoveredOccasion(null)}
                  onFocus={() => setHoveredOccasion(label)}
                  onBlur={() => setHoveredOccasion(null)}
                  style={{ animationDelay: `${0.8 + index * 0.2}s` }}
                >
                  <span className={styles.occasionIcon}>
                    <Icon
                      size={20}
                      strokeWidth={1.9}
                      animate={hoveredOccasion === label || isSelected}
                    />
                  </span>
                  <span className={styles.occasionLabel}>{label}</span>
                  <span className={styles.occasionCheck} aria-hidden="true">
                    <CheckIcon />
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Reserved so the panel above doesn't shift when the button mounts
            after the fade-in finishes. */}
        <div className={styles.actionSlot}>
          {gridAnimationComplete && (
            <button
              type="button"
              className={cn(theme.primary, styles.continueButton)}
              onClick={handleContinue}
              disabled={selectedOccasions.length === 0}
              style={{
                opacity: selectedOccasions.length === 0 ? 0.5 : 1,
                cursor: selectedOccasions.length === 0 ? 'not-allowed' : 'pointer',
              }}
            >
              Continue
            </button>
          )}
        </div>
      </div>
    </AuthPage>
  );
};

export default ChooseOccasion;
